<template>
	<span v-if="description" class="c-info-button">
		<!-- The button -->
		<BaseButton
			class="
				order-2
				w-full
				h-full
				border-2 border-theme
				p-0
				pointer-events-auto
			"
			:class="{
				'text-theme bg-transparent hover:hover:bg-theme-10': !active,
			}"
			aria-label="forklaring"
			:aria-describedby="id"
			@click="() => setActive(true)"
			@focus="() => setActive(true)"
			@mouseover="() => setActive(true)"
			@focusout="() => setActive(false)"
			@mouseleave="() => setActive(false)"
		>
			<SvgIconQuestion class="w-8 h-12" />
		</BaseButton>

		<!-- The message -->
		<TransitionExt name="fade-in-out" duration="200">
			<div
				v-show="active"
				:id="id"
				role="tooltip"
				class="c-info-button__tooltip"
				:class="{
					'c-info-button__tooltip--above items-end': showAbove,
					'c-info-button__tooltip--below items-start': !showAbove,
				}"
			>
				<div
					class="
						relative
						flex-shrink-0
						bg-white
						rounded-8
						min-w-2/12col
						px-3xs/h
						py-4xs/v
						hyphens-auto
						break-words
						whitespace-pre-wrap
						text-text text-label-sm
					"
					:style="{
						transform: `translate(${offset.x}%, ${offset.y}px)`,
						width: `${boxWidth}px`,
						'--counter-offset-x': `${-offset.x}%`,
					}"
					v-html="description"
				></div>
			</div>
		</TransitionExt>
	</span>
</template>

<script>
import { viewportData } from '~/plugins/visual-viewport-detection.client.js';
import SvgIconQuestion from '~/assets/svgs/icon-question.svg?inline';

export default {
	name: 'InfoButton',

	components: {
		SvgIconQuestion,
	},

	props: {
		id: {
			type: String,
			required: true,
		},
		description: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			active: false,
			showAbove: false,
			offset: {
				x: 0,
				y: 0,
			},
		};
	},
	computed: {
		boxWidth() {
			return Math.max(
				120,
				Math.min((this.description?.length || 1) * 2, 240)
			);
		},
		viewportData() {
			return viewportData;
		},
	},
	watch: {
		active() {
			this.updateRelations();
		},
		viewportData() {
			process.client && this.updateRelations();
		},
	},

	mounted() {
		window.addEventListener('scroll', this.updateRelations);
		this.updateRelations();
	},
	beforeDestroy() {
		window.removeEventListener('scroll', this.updateRelations);
	},

	methods: {
		setActive(active = !this.active) {
			this.active = active;
		},
		updateRelations() {
			if (this.$el && this.active) {
				const { top, right, bottom } = this.$el.getBoundingClientRect();
				const y = (top + bottom) / 2;
				this.showAbove = y > window.innerHeight / 2;

				this.offset.x =
					(Math.max(
						-(this.boxWidth / 2),
						Math.min(
							window.innerWidth - right - this.boxWidth / 2,
							0
						)
					) /
						this.boxWidth) *
					100;
				this.offset.y = -window.scrollY + (this.showAbove ? -30 : 30);
			}
		},
	},
};
</script>

<style lang="postcss">
.c-info-button {
	@apply inline-flex justify-center items-center w-32 h-32 cursor-help pointer-events-none;
}
.c-info-button__tooltip {
	@apply fixed inline-flex justify-center items-center w-1 h-1;
	z-index: 1000;
	filter: drop-shadow(0px 2px 12px #00000000);

	& > :before {
		@apply absolute;
		content: '';
		left: calc(50% + var(--counter-offset-x, 0px));
		transform: translateX(-50%);
	}
}
.c-info-button__tooltip--above > :before {
	top: 100%;
	width: 0px;
	height: 0px;
	border-style: solid;
	border-width: 10px 6px 0 6px;
	border-color: #fff transparent transparent transparent;
}
.c-info-button__tooltip--below > :before {
	bottom: 100%;
	width: 0px;
	height: 0px;
	border-style: solid;
	border-width: 0 6px 10px 6px;
	border-color: transparent transparent #fff transparent;
}
</style>
